import { z } from 'zod';
import type { FilterCondition } from '../flow/filter';
import type { FilterFieldCatalogEntry } from '../flow/filter-fields';
import { isValidOperatorForType, validateOperatorValueShape } from '../flow/filter-operators';
import {
  LOOP_FILTER_FIELDS,
  LoopFilterSchema,
  MINE_LOOP_FILTER,
  isMineLoopFilter,
  loopFilterKind,
  type LoopFilter,
} from '../open-loops/loop-filters';
import { RECENT_WINDOW_DAYS } from '../open-loops/recency';
import { safeParseJson } from '../parse-json';
import { OWNERSHIP_ARM_IDS, OWNERSHIP_ARM_LABEL, type OwnershipArmId } from '../owned-accounts/arms';

/**
 * The Home board's rules, as one filter group per column.
 *
 * The board has four places a rule can live: the Board itself (which accounts, how far
 * back), Today's Meetings, Open Loops and Cold Accounts. Each is a `LoopFilter` (the
 * `{ groups, logic }` the ledger toolbar's Filters control already edits), so the board's
 * control is the same control with a catalog that names these fields. Open Loops stays a
 * real loop filter, evaluated by `loopFilterPredicate` unchanged.
 *
 * A missing column means that column's default, so `{}` is today's board exactly; the
 * defaults are the numbers the board has always used. Across columns there is no And/Or:
 * every column's rules apply to its own column.
 *
 * Shared, so the browser builds the filter and the server compiles the same one.
 */

export const HOME_FILTER_COLUMNS = ['board', 'meetings', 'loops', 'cold'] as const;
export type HomeFilterColumn = (typeof HOME_FILTER_COLUMNS)[number];

/** Today's windows. The board's constants in `services/home/constants.ts` are these. */
export const HOME_WINDOW_DEFAULT_DAYS = 28;
export const HOME_HORIZON_DEFAULT_DAYS = 5;
/**
 * What the server allows. The window cannot reach past what the Open Loops read keeps (`recencyCutoffDate`:
 * RECENT_WINDOW_DAYS plus the day of timezone slack), so it is derived from that, never its own number.
 * A value over these is a 400, never a clamp; removing the rule means these.
 */
export const HOME_WINDOW_MAX_DAYS = RECENT_WINDOW_DAYS + 1;
export const HOME_HORIZON_MAX_DAYS = 30;

export const HOME_ATTENDANCE = ['owner', 'attendee'] as const;
export type HomeAttendance = (typeof HOME_ATTENDANCE)[number];
const ATTENDANCE_LABEL: Record<HomeAttendance, string> = { owner: 'I own the meeting', attendee: "I'm an attendee" };

export const HomeBoardFilterSchema = z.strictObject({
  board: LoopFilterSchema.optional(),
  meetings: LoopFilterSchema.optional(),
  loops: LoopFilterSchema.optional(),
  cold: LoopFilterSchema.optional(),
});
export type HomeBoardFilter = z.infer<typeof HomeBoardFilterSchema>;
/** Every column present: what {@link resolveHomeBoardFilter} returns. */
export type ResolvedHomeBoardFilter = Required<HomeBoardFilter>;

/** The re-grouped loop fields whose fixed choices show as a list to tick. The Open Loops page's own catalog is untouched. */
const CLOSED_LIST_LOOP_FIELDS = new Set(['responsibility', 'priority']);

interface FieldCap { max: number }

const COLUMN_FIELDS: Record<HomeFilterColumn, FilterFieldCatalogEntry[]> = {
  board: [
    {
      key: 'accounts',
      label: 'Accounts',
      group: 'Board',
      type: 'picklist',
      values: OWNERSHIP_ARM_IDS.map((id) => ({ value: id, label: OWNERSHIP_ARM_LABEL[id] })),
      allowedOperators: ['in_any_of'],
      valueInput: 'closed-list',
    },
    { key: 'window_days', label: 'Look back (days)', group: 'Board', type: 'number', allowedOperators: ['equals'] },
  ],
  meetings: [
    { key: 'starts', label: 'Starts', group: 'Meetings', type: 'date', allowedOperators: ['in_next_days'] },
    {
      key: 'attendance',
      label: 'Attendance',
      group: 'Meetings',
      type: 'picklist',
      values: HOME_ATTENDANCE.map((value) => ({ value, label: ATTENDANCE_LABEL[value] })),
      allowedOperators: ['in_any_of'],
      valueInput: 'closed-list',
    },
  ],
  // The shared entries are re-grouped by copy: the Open Loops page keeps its own 'Loop' label.
  loops: LOOP_FILTER_FIELDS.map((f) => ({
    ...f,
    group: 'Open Loops',
    ...(CLOSED_LIST_LOOP_FIELDS.has(f.key) ? { valueInput: 'closed-list' as const } : {}),
  })),
  cold: [{ key: 'next_meeting', label: 'Next meeting', group: 'Cold Accounts', type: 'date', allowedOperators: ['not_in_next_days'] }],
};

const FIELD_CAPS: Record<string, FieldCap> = {
  window_days: { max: HOME_WINDOW_MAX_DAYS },
  starts: { max: HOME_HORIZON_MAX_DAYS },
  next_meeting: { max: HOME_HORIZON_MAX_DAYS },
};

/** Ordered by column, so the field picker folds consecutive `group` headers into one section each. */
export const HOME_FILTER_FIELDS: FilterFieldCatalogEntry[] = HOME_FILTER_COLUMNS.flatMap((c) => COLUMN_FIELDS[c]);

const FIELD_BY_KEY = new Map(HOME_FILTER_FIELDS.map((f) => [f.key, f]));
const COLUMN_BY_FIELD = new Map<string, HomeFilterColumn>(HOME_FILTER_COLUMNS.flatMap((c) => COLUMN_FIELDS[c].map((f) => [f.key, c] as const)));

export function columnOfField(key: string): HomeFilterColumn | null {
  return COLUMN_BY_FIELD.get(key) ?? null;
}

function rule(id: string, field: string, operator: string, value: FilterCondition['value']): LoopFilter['groups'][number] {
  return { id, logic: 'AND', conditions: [{ id: `${id}-c`, field, operator, value }] };
}

export const DEFAULT_HOME_FILTER: ResolvedHomeBoardFilter = {
  board: { groups: [rule('window', 'window_days', 'equals', HOME_WINDOW_DEFAULT_DAYS)], logic: 'AND' },
  meetings: {
    groups: [
      rule('starts', 'starts', 'in_next_days', HOME_HORIZON_DEFAULT_DAYS),
      rule('attendance', 'attendance', 'in_any_of', [...HOME_ATTENDANCE]),
    ],
    logic: 'AND',
  },
  loops: MINE_LOOP_FILTER,
  cold: { groups: [rule('next-meeting', 'next_meeting', 'not_in_next_days', HOME_HORIZON_DEFAULT_DAYS)], logic: 'AND' },
};

/** `partial` with every missing column filled by its default. */
export function resolveHomeBoardFilter(partial: HomeBoardFilter): ResolvedHomeBoardFilter {
  return {
    board: partial.board ?? DEFAULT_HOME_FILTER.board,
    meetings: partial.meetings ?? DEFAULT_HOME_FILTER.meetings,
    loops: partial.loops ?? DEFAULT_HOME_FILTER.loops,
    cold: partial.cold ?? DEFAULT_HOME_FILTER.cold,
  };
}

// An array is its own shape: `['{me}']` is a literal name list, `'{me}'` is Me, so they must not compare equal.
const valueKey = (v: FilterCondition['value']) => (Array.isArray(v) ? `[${JSON.stringify([...v].map(String).sort())}]` : String(v));
const condKey = (c: FilterCondition) => `${c.field}|${c.operator}|${valueKey(c.value)}`;
const activeGroups = (f: LoopFilter) => f.groups.filter((g) => g.conditions.length > 0);

/** What a column asks for, ignoring ids, labels and order. Loops compare as groups; the other columns as one rule per field. */
function unitsOf(column: HomeFilterColumn, filter: LoopFilter): string[] {
  const groups = activeGroups(filter);
  if (column === 'loops') {
    const units = groups.map((g) => `${g.logic}[${g.conditions.map(condKey).sort().join(';')}]`);
    if (groups.length > 1 && filter.logic === 'OR') units.push('logic:OR');
    return units.sort();
  }
  return groups.flatMap((g) => g.conditions.map(condKey)).sort();
}

/** How many rules of `filter` differ from `defaults`: a changed value is one, a removed or added rule is one. */
function columnDiff(column: HomeFilterColumn, filter: LoopFilter, defaults: LoopFilter): number {
  const have = unitsOf(column, filter);
  const want = unitsOf(column, defaults);
  if (column === 'loops') {
    const left = (a: string[], b: string[]) => {
      const rest = [...b];
      return a.filter((u) => { const i = rest.indexOf(u); if (i < 0) return true; rest.splice(i, 1); return false; }).length;
    };
    return Math.max(left(want, have), left(have, want));
  }
  const byField = (units: string[]) => new Map(units.map((u) => [u.split('|')[0], u]));
  const a = byField(have);
  const b = byField(want);
  let diff = 0;
  for (const k of new Set([...a.keys(), ...b.keys()])) if (a.get(k) !== b.get(k)) diff += 1;
  return diff;
}

/** The columns of `filter` that differ from their default. */
function changedColumns(filter: HomeBoardFilter): HomeFilterColumn[] {
  const full = resolveHomeBoardFilter(filter);
  return HOME_FILTER_COLUMNS.filter((c) => columnDiff(c, full[c], DEFAULT_HOME_FILTER[c]) > 0);
}

/** Only the columns that differ from their default: the smallest filter that means the same thing. */
export function compactHomeBoardFilter(filter: HomeBoardFilter): HomeBoardFilter {
  const full = resolveHomeBoardFilter(filter);
  const out: HomeBoardFilter = {};
  for (const c of changedColumns(filter)) out[c] = full[c];
  return out;
}

/** Badge count: the rules that differ from the defaults, so an untouched board shows none. */
export function homeFilterDiffCount(filter: HomeBoardFilter): number {
  const full = resolveHomeBoardFilter(filter);
  return HOME_FILTER_COLUMNS.reduce((n, c) => n + columnDiff(c, full[c], DEFAULT_HOME_FILTER[c]), 0);
}

/** What kind of filter is applied, for analytics and the Claude handoff: never its contents. */
export function homeFilterKind(filter: HomeBoardFilter): { loops: 'mine' | 'all' | 'custom'; changedColumns: HomeFilterColumn[] } {
  return { loops: loopFilterKind(resolveHomeBoardFilter(filter).loops), changedColumns: changedColumns(filter) };
}

/** A loops-only filter (the Open Loops page's, or `my_day`'s `loop_filter`) as a board filter: nothing for Mine, which is the default. */
export function liftLoopFilter(loops: LoopFilter): HomeBoardFilter {
  return isMineLoopFilter(loops) ? {} : { loops };
}

/** One column written key by key in a fixed order, every group, condition, value, id and label exactly as given. */
function orderedColumn(filter: LoopFilter): LoopFilter {
  return {
    groups: filter.groups.map((g) => ({
      id: g.id,
      ...(g.label !== undefined ? { label: g.label } : {}),
      logic: g.logic,
      ...(g.boxed !== undefined ? { boxed: g.boxed } : {}),
      conditions: g.conditions.map((c) => ({ id: c.id, field: c.field, operator: c.operator, value: c.value })),
    })),
    logic: filter.logic,
  };
}

/**
 * The one wire / cache-key / storage form: compact (a column equal to its default drops out),
 * columns in catalog order, keys in a fixed order. Nothing is sorted, regenerated or rewritten:
 * value lists, ids, labels, groups and conditions keep exactly the order and content given, because
 * order can carry meaning (`equals` compares to the first value). Filters that mean the same thing but
 * were built in a different order may therefore serialize differently; that only costs an extra
 * refetch, never a wrong result. Parsing the string back and writing it again gives the same string.
 * A column with no rules stays present as `groups: []`: a removed rule is not the default.
 */
export function serializeHomeBoardFilter(filter: HomeBoardFilter): string {
  const compact = compactHomeBoardFilter(filter);
  const ordered: HomeBoardFilter = {};
  for (const c of HOME_FILTER_COLUMNS) {
    const col = compact[c];
    if (col) ordered[c] = orderedColumn(col);
  }
  return JSON.stringify(ordered);
}

const MIXED_COLUMNS = 'A filter group can only hold fields from one column';

/**
 * The first problem with `filter`, or null when every condition names a field of the
 * column it sits in, an operator that field allows, a value of the right shape, and a
 * day count inside the cap. The API answers a 400 with it: a bad filter is never
 * half-applied, and an over-cap value is refused rather than clamped.
 */
export function validateHomeBoardFilter(filter: HomeBoardFilter): string | null {
  for (const column of HOME_FILTER_COLUMNS) {
    const f = filter[column];
    if (!f) continue;
    const seen = new Set<string>();
    if (column !== 'loops' && f.logic !== 'AND') return `${column}: rules in this column are all required together`;
    for (const group of f.groups) {
      const columns = new Set(group.conditions.map((c) => columnOfField(c.field)).filter((c) => c !== null));
      if (columns.size > 1) return MIXED_COLUMNS;
      if (column !== 'loops' && group.logic === 'OR' && group.conditions.length > 1) return `${column}: rules in this column are all required together`;
      for (const cond of group.conditions) {
        const field = FIELD_BY_KEY.get(cond.field);
        if (!field) return `Unknown home filter field "${cond.field}"`;
        const owner = columnOfField(cond.field);
        if (owner !== column) return `"${cond.field}" belongs to the ${owner} column, not ${column}`;
        if (!isValidOperatorForType(field.type, cond.operator) || (field.allowedOperators && !field.allowedOperators.includes(cond.operator))) {
          return `Operator "${cond.operator}" is not allowed for "${cond.field}"`;
        }
        const values = field.values?.map((v) => (typeof v === 'string' ? v : v.value));
        const shapeError = validateOperatorValueShape(field.type, cond.operator, cond.value, values);
        if (shapeError) return `"${cond.field}": ${shapeError}`;
        const cap = FIELD_CAPS[cond.field];
        if (cap) {
          // Number(['14']) is 14: an array or null is not a day count, whatever it coerces to.
          const scalar = typeof cond.value === 'number' || typeof cond.value === 'string';
          const n = scalar ? Number(cond.value) : NaN;
          if (!Number.isInteger(n) || n < 1 || n > cap.max) return `"${cond.field}" must be a whole number of days from 1 to ${cap.max}`;
        }
        if (column !== 'loops') {
          if (seen.has(cond.field)) return `"${cond.field}" can only be set once`;
          seen.add(cond.field);
        }
      }
    }
  }
  return null;
}

/** A `HomeBoardFilter` that also passes {@link validateHomeBoardFilter}: the one schema every boundary parses a filter with. */
export const ValidHomeBoardFilterSchema = HomeBoardFilterSchema.superRefine((filter, ctx) => {
  const error = validateHomeBoardFilter(filter);
  if (error) ctx.addIssue({ code: 'custom', message: error });
});

/** The filter in `raw`, or null when it is not JSON of the right shape or breaks a home filter rule. */
export function parseValidHomeBoardFilter(raw: string | null | undefined): HomeBoardFilter | null {
  if (!raw) return null;
  const parsed = safeParseJson(ValidHomeBoardFilterSchema, raw);
  return parsed.success ? parsed.data : null;
}

/** `GET /api/v4/home` query: the board's Filters control as compact JSON, refused when it is not a valid filter. */
export const HomeQuerySchema = z.object({
  filters: z
    .string()
    .transform((raw, ctx) => {
      const parsed = safeParseJson(ValidHomeBoardFilterSchema, raw);
      if (!parsed.success) {
        ctx.addIssue({ code: 'custom', message: parsed.error.message });
        return z.NEVER;
      }
      return parsed.data;
    })
    .optional(),
});

/** What the server reads a (valid) filter as. */
export interface HomeRules {
  /** The ownership routes the rep narrowed to; null means every route. */
  arms: ReadonlySet<OwnershipArmId> | null;
  /** One window for loop age, account-quiet and signal age. */
  windowDays: number;
  meetings: { horizonDays: number; attendance: ReadonlySet<HomeAttendance> };
  /** Evaluated by `loopFilterPredicate`, unchanged. */
  loops: LoopFilter;
  cold: { noMeetingWithinDays: number };
}

/**
 * The rules a filter stands for. A column's missing rule is not "unset": a column that is
 * present without its window or look-ahead means the rep removed it, so the server cap
 * applies; a missing COLUMN means its default. With no Cold rule, Cold follows the
 * meetings look-ahead, as it always has.
 */
export function compileHomeRules(filter: HomeBoardFilter): HomeRules {
  const error = validateHomeBoardFilter(filter);
  if (error) throw new Error(`Invalid home filter: ${error}`);
  const full = resolveHomeBoardFilter(filter);
  const conditions = (c: HomeFilterColumn) => full[c].groups.flatMap((g) => g.conditions);
  const find = (c: HomeFilterColumn, field: string) => conditions(c).find((x) => x.field === field);
  const list = (cond: FilterCondition | undefined) => (cond && Array.isArray(cond.value) ? cond.value : null);

  const arms = list(find('board', 'accounts'));
  const window = find('board', 'window_days');
  const starts = find('meetings', 'starts');
  const attendance = list(find('meetings', 'attendance'));
  const nextMeeting = find('cold', 'next_meeting');

  const horizonDays = starts ? Number(starts.value) : HOME_HORIZON_MAX_DAYS;
  return {
    arms: arms ? new Set(arms as OwnershipArmId[]) : null,
    windowDays: window ? Number(window.value) : HOME_WINDOW_MAX_DAYS,
    meetings: { horizonDays, attendance: new Set((attendance ?? HOME_ATTENDANCE) as readonly HomeAttendance[]) },
    loops: full.loops,
    cold: { noMeetingWithinDays: nextMeeting ? Number(nextMeeting.value) : horizonDays },
  };
}
